/* eslint max-params: 0 */

import React from 'react';
import get from 'lodash.get';
import { createSelector } from 'reselect';

// Components
import { Sanitizer } from 'acq-components/lib/base-components/Sanitizer/Sanitizer';
import InFlowAlertBanner from 'src/universal/components/InFlowAlertBanner';
import DescriptionList from 'src/universal/components/DescriptionList';
import { Element } from '@americanexpress/aqx-components';
import { SectionTitle } from 'page_modules/business-pdp/components/SectionTitle';

// Selectors
import {
  getPageData,
  getPageFirstCardData,
  getPageLabels,
  getPageLinks,
  getPageEep,
  getPageGlobalData,
  getPageFeatureFlags,
  getPageAllowedLists,
  getIsSuppCardsModalOpen,
  getPznOfferEligibleDtm,
  getPageDisallowedLists
} from 'src/universal/redux/page';
import { getSpecialOfferList } from 'src/universal/redux/pznData';
import {
  getCardMemberCards,
  isGCSMember,
  isLegacyGcsCardMember
} from 'src/universal/redux/cardMemberInfo';
import {
  isProspect as getIsProspect,
  isLoggedOut
} from 'src/universal/redux/userType';
import {
  getPznOfferList,
  hasPznDataArrived,
  getPznOffersEepByPmc
} from 'src/universal/redux/pznData';
import {
  getCardColorInfoList,
  determineColorPicker
} from 'src/universal/redux/cardColors';

// Utils
import buildAccordionDtmData from 'src/universal/utils/buildAccordionAnalyticsData';
import textRemoveTags from 'service_modules/utils/text-remove-tags';
import { decideCardArtUrl } from 'src/universal/utils/cardColorChoices';
import parseArrayToObject from 'src/universal/utils/parseArrayToObject';
import { formatLinksWithEep } from '../utils';
import { getXSellCardsExtItems } from '../utils/cmExtHelper';
import { buildStickySideRailProps } from 'page_modules/business-pdp/utils/stickySideRailProps';
import {
  buildXSellCardList,
  hasPznSuppliedXSellData
} from '../utils/xSellDataHelper';
import { getUpgradeCtaProps } from 'src/universal/utils/orchestraUtils';
import {
  capitalizeButtonText,
  formatSpecialOfferButtonText,
  formatLTOOfferButtonText
} from 'src/universal/utils/formatOfferButton';
import replaceStringWithVars from 'src/universal/utils/replaceStringWithVars';
import { isDualOffer } from 'src/universal/utils/offerStyling';

// Styles
import accordionStyles from '../components/BenefitsAccordion/__styles__/benefitsAccordion.scss?modules';
import { isG171Experiment } from 'src/universal/components/Experiments/G171/redux';

export const getPdpCardShortName = createSelector(
  getPageFirstCardData,
  ({ shortName = '' }) => shortName
);

export const getPdpOfferData = createSelector(
  [hasPznDataArrived, getPdpCardShortName, getPznOfferList],
  (hasPznArrived, shortName, pznOfferList) => {
    return hasPznArrived
      ? pznOfferList.find(
          offer => offer.shortName === shortName && offer.header && offer.description
        )
      : undefined;
  }
);

export const havingDualOffer = createSelector([getPdpOfferData], offerData => {
  if (offerData) {
    return isDualOffer(offerData);
  }
  return false;
});

export const getPdpLinks = urlEep =>
  createSelector(
    [
      getPageFirstCardData,
      getPageLinks,
      getPageEep(urlEep),
      getPznOffersEepByPmc,
      getPdpOfferData
    ],
    (cardData, pageLinks, eep, pznOffersEepByPmc, pznOffer) => {
      const links = { ...parseArrayToObject(cardData.links, 'key'), ...pageLinks };
      cardData.pznOffer = pznOffer;
      return formatLinksWithEep({
        links: links,
        eep: pznOffersEepByPmc[cardData.pmcCode] || eep,
        appendEep: false,
        cardData
      });
    }
  );

const getDynamicEep = createSelector(
  [getPageFirstCardData, getPageEep(), getPznOffersEepByPmc],
  (cardData, eep, pznOffersEepByPmc) => pznOffersEepByPmc[cardData.pmcCode] || eep
);

export const getRabBannerLinks = createSelector(
  getPdpLinks(),
  cardLinks => cardLinks.referABusiness || {}
);

export const getPdpFeaturedBenefitsData = createSelector(
  getPageFirstCardData,
  cardData => cardData.featuredBenefits || []
);

export const getPdpHowYouEarnData = createSelector(
  getPageFirstCardData,
  ({ howYouEarn }) => {
    if (!howYouEarn) {
      return {};
    }
    const trailingHeaderContent = howYouEarn.desc || [];
    return {
      ...howYouEarn,
      tooltip: trailingHeaderContent.find(
        ({ type }) => type && type.toLowerCase() === 'tooltip'
      ),
      flag: trailingHeaderContent.find(
        ({ type }) => type && type.toLowerCase() === 'flag'
      ),
      slides: (get(howYouEarn, 'content') || []).map(slide => {
        const trailingDescriptionContent = slide.desc || [];
        const tooltip = trailingDescriptionContent.find(
          ({ type }) => type.toLowerCase() === 'tooltip'
        );
        return {
          ...slide,
          title: slide.title,
          header: slide.header,
          subHeader: slide.subHeader,
          tooltip
        };
      }),
      dtm: parseArrayToObject(howYouEarn.dtmEvents, 'tag') || {}
    };
  }
);

export const getPdpHowYouEarnMobileHeaderModalText = createSelector(
  getPdpHowYouEarnData,
  ({ tooltip }) => get(tooltip, 'text') || ''
);

const getPdpBenefitsAccordionData = createSelector(
  getPageFirstCardData,
  cardData => cardData.cardBenefits || {}
);

export const getPdpApplyModalData = createSelector(
  [getPageFirstCardData, getPdpLinks()],
  (cardData, cardLinks) => ({
    ...(cardData.interstitialToApp || {}),
    applyButton: cardLinks.applyModalAccept,
    cancelButton: cardLinks.applyModalCancel,
    shortName: cardData.shortName
  })
);

export const getPdpReinforcementBannerData = createSelector(
  getPageFirstCardData,
  cardData => ({
    ...cardData.marketingBanner,
    assets: parseArrayToObject(
      get(cardData, 'marketingBanner.assets'),
      'breakpoint'
    ),
    cardArt: get(cardData, 'appAssets.cardArt') || null,
    cardTitle: get(cardData, 'cardHeadlines.title') || '',
    cardAlt: get(cardData, 'cardHeadline.summitCardAlt') || '',
    shortName: cardData.shortName
  })
);

export const getPdpCrossSellBannerData = createSelector(
  getPageFirstCardData,
  cardData => {
    const links = parseArrayToObject(cardData.links, 'key');
    return {
      ...cardData.reinforcementBanner,
      scrollDtm: get(links, 'crossSell.dtm') || {},
      cardTitle: get(cardData, 'cardHeadlines.title') || ''
    };
  }
);

export const getCardWithPznData = createSelector(
  [getPageFirstCardData, getPdpCardShortName, getPznOfferList],
  (cardObj, shortName, offerList = []) => {
    const offer = offerList.find(o => o.shortName === shortName);
    const pznOffer = offer ? { ...offer } : null; //prefer make a copy if found
    return { ...cardObj, pznOffer };
  }
);

const getProjectCharmData = createSelector(
  [getPageFeatureFlags, getPageAllowedLists, getPdpCardShortName],
  (featureFlags, allowedlists, shortName) => ({
    projectCharmActive: featureFlags.projectCharm,
    isProjectCharmCard: (allowedlists.projectCharm || []).find(
      cardName => cardName === shortName
    )
  })
);

export const getAprRateData = createSelector(
  [
    hasPznDataArrived,
    getPdpCardShortName,
    getPznOfferList,
    getPageLabels,
    getProjectCharmData
  ],
  (hasPznArrived, shortName, pznOfferList, labels, projectCharmData) => {
    if (hasPznArrived) {
      const rate = (
        pznOfferList.find(offer => offer.shortName === shortName && offer.rate) || {}
      ).rate;
      const { projectCharmActive, isProjectCharmCard } = projectCharmData;
      if (rate) {
        return {
          rate,
          header:
            projectCharmActive && isProjectCharmCard
              ? labels.payOverTime
              : labels.introAprTitle
        };
      }
    }
  }
);

export const shouldRenderAprBoxForCard = createSelector(
  [getPageDisallowedLists, getPageFirstCardData, getPznOfferList],
  (disallowedList, cardData, pznOfferList) => {
    const disallowedCards = disallowedList?.aprBox ?? [];
    const offer = (pznOfferList || []).find(o => o.shortName === cardData.shortName);
    return disallowedCards.indexOf(cardData.shortName) === -1 && !isDualOffer(offer);
  }
);

export const getFeeData = createSelector(
  [getPageFirstCardData, getProjectCharmData],
  (cardObj, projectCharmData) => {
    const { projectCharmActive, isProjectCharmCard } = projectCharmData;
    if (!projectCharmActive || !isProjectCharmCard) {
      return cardObj.rate || '';
    }
  }
);

const eepDescription = (eep, string) => {
  return replaceStringWithVars({
    varsObject: { eep: eep },
    string: string
  });
};

const buildBenefit = (benefit, textContent, eepCode) => {
  const subItemCls = accordionStyles[`subItem_${benefit.key || ''}`];
  if (benefit.key === 'inFlowAlertBannerLocationVariant') {
    return (
      <Element className={subItemCls}>
        <Sanitizer Component="span" safelySetInnerHTML={textContent} />
        <InFlowAlertBanner />
      </Element>
    );
  }
  if (benefit.descriptionList) {
    return (
      <Element className={subItemCls}>
        <Sanitizer Component="span" safelySetInnerHTML={textContent} />
        <DescriptionList
          list={benefit.descriptionList}
          styles={accordionStyles.listContainer}
        />
      </Element>
    );
  }
  if (benefit.content) {
    const { content = [] } = benefit;
    return content.map(({ description, descriptionList = [] }) => (
      <Element key={description} className={subItemCls}>
        {description && (
          <Sanitizer
            Component="span"
            safelySetInnerHTML={eepDescription(eepCode, description)}
          />
        )}
        {descriptionList && (
          <DescriptionList
            list={descriptionList}
            styles={accordionStyles.listContainerVariant}
          />
        )}
      </Element>
    ));
  }

  return (
    <Sanitizer
      className={subItemCls}
      Component="span"
      safelySetInnerHTML={textContent}
    />
  );
};

const composeCardBenefitProps = (
  data = [],
  isMobile = false,
  dynamicEep,
  cardData = {}
) => {
  const parseBenefit = (benefit = {}) => {
    const disclaimer = benefit.disclaimer ? `<br><br>${benefit.disclaimer}` : '';
    const needOuterH4 = !isMobile && benefit.header;
    const benefitHeader = `<span>${benefit.header || ''}</span>${
      benefit.tag
        ? `<span class="${accordionStyles.flag}">${benefit.tag}</span>`
        : ''
    }`;
    const benefitDescription = `${benefit.description || ''}${
      benefit.subTag
        ? `<span class="${accordionStyles.flag}">${benefit.subTag}</span>`
        : ''
    }${disclaimer}`;
    return {
      title: isMobile ? (
        <Sanitizer
          className={accordionStyles.itemTitle}
          Component="h4"
          safelySetInnerHTML={benefitHeader}
        />
      ) : needOuterH4 ? (
        <Sanitizer
          className={accordionStyles.itemTitle}
          Component="h4"
          safelySetInnerHTML={benefitHeader}
        />
      ) : (
        benefitHeader
      ),
      text: buildBenefit(benefit, benefitDescription, dynamicEep)
    };
  };

  const items = data.map((benefitGroup = {}) => {
    const title = get(benefitGroup, 'button.title') || '';
    const description = get(benefitGroup, 'description') || '';

    const icon = {
      directory: 'dls',
      imageName: get(benefitGroup, 'imageName') || ''
    };

    const content = get(benefitGroup, 'content') || [];

    const actionTracking = {
      action: get(benefitGroup, 'button.actionTracking.action') || '',
      attribute: get(benefitGroup, 'button.actionTracking.attribute') || ''
    };

    const dtm = {
      eventAction: get(benefitGroup, 'button.dtm.eventAction') || '',
      eventAsset: get(benefitGroup, 'button.dtm.eventAsset') || ''
    };

    return {
      icon,
      backBtnAriaLabel:
        isMobile &&
        `Back to ${textRemoveTags(cardData.cardHeadline)} product detail page`,
      label: `${title || ''} ${(!isMobile && description) || ''}`,
      body: content.map(parseBenefit),
      actionTracking,
      dtm
    };
  });

  return { items };
};

export const getAccordionData = createSelector(
  [getPdpBenefitsAccordionData, getDynamicEep],
  (benefitsData, dynamicEep) =>
    composeCardBenefitProps(benefitsData, false, dynamicEep)
);

export const getMobileAccordionData = createSelector(
  [getPdpBenefitsAccordionData, getDynamicEep, getPageFirstCardData],
  (benefitsData, dynamicEep, cardData) =>
    composeCardBenefitProps(benefitsData, true, dynamicEep, cardData)
);

export const getAccordionImpressionDtm = createSelector(
  getPageData,
  pageData => get(pageData, 'allBenefits.dtm') || {}
);

export const getShopComponentsAnalyticsData = createSelector(
  getPdpLinks(),
  ({ benefitAccordion = {} }) => {
    return {
      ...buildAccordionDtmData(benefitAccordion)
    };
  }
);

export const getPdpCrossSellDataCM = (urlEep, styles = {}) =>
  createSelector(
    [
      getPznOfferList,
      getPageEep(urlEep),
      getPageLabels,
      getPznOffersEepByPmc,
      getPageFirstCardData,
      getPageAllowedLists,
      getCardColorInfoList
    ],
    (
      offerList,
      eep,
      pageLabels,
      pznOffersEepByPmc,
      cardData,
      allowedlists,
      colorInfoList
    ) => {
      let offerListXSell = (offerList || []).slice(1);

      if (!hasPznSuppliedXSellData(offerListXSell)) {
        offerListXSell = cardData.similarCards;
      }

      return {
        products: buildXSellCardList(
          offerListXSell,
          eep,
          pznOffersEepByPmc,
          styles,
          cardData.shortName,
          get(allowedlists, 'keepWordsInCardFee') || [],
          colorInfoList
        ),
        title: <SectionTitle text={pageLabels.compareCards} />,
        cmExtItems: getXSellCardsExtItems(offerListXSell)
      };
    }
  );

export const getPdpCrossSellData = (urlEep, styles = {}) =>
  createSelector(
    [
      getPageFirstCardData,
      getPageEep(urlEep),
      getPageLabels,
      getPznOffersEepByPmc,
      getPageAllowedLists,
      getCardColorInfoList
    ],
    (cardData, eep, pageLabels, pznOffersEepByPmc, allowedlists, colorInfoList) => {
      return {
        products: buildXSellCardList(
          cardData.similarCards,
          eep,
          pznOffersEepByPmc,
          styles,
          cardData.shortName,
          get(allowedlists, 'keepWordsInCardFee') || [],
          colorInfoList
        ),
        title: <SectionTitle text={pageLabels.compareCards} />
      };
    }
  );

export const getPerceptionOfCoverageData = createSelector(
  getPageGlobalData,
  globalData => {
    return get(globalData, 'globalHeaderPage.perceptionOfCoverage.pocGray');
  }
);

export const getAddAnEmployeeDtmEvents = createSelector(getPageLinks, pageLinks =>
  parseArrayToObject(get(pageLinks, 'cmAddAnEmployee.dtmEvents'), 'tag')
);

export const getAddAnEmployeeHoverText = createSelector(
  [getPageLabels, getPageFirstCardData],
  (pageLabels, currCard) => {
    const rawLabel =
      pageLabels.addEmpCardsLinkTitle ?? 'Click here to add an employee card to';
    return `${rawLabel} ${textRemoveTags(currCard.cardHeadline)}`;
  }
);

export const getCurrentCardInfo = createSelector(
  [
    getCardWithPznData,
    getCardMemberCards,
    isGCSMember,
    isLegacyGcsCardMember,
    getAddAnEmployeeDtmEvents,
    getAddAnEmployeeHoverText,
    getIsSuppCardsModalOpen
  ],
  (
    currentCard,
    userCards,
    gcsMember,
    gcsLegacy,
    addAnEmployeeScrollDtm,
    addEmpCardHoverText,
    suppModalOpen
  ) => {
    const hasPznOffer = !!currentCard.pznOffer;
    const isCardMemberCard = !!(userCards || []).find(
      c => c.pmcCode === currentCard.pmcCode
    );

    return {
      currentCard,
      isCardMemberCard,
      hasPznOffer,
      addAnEmployeeScrollDtm,
      addEmpCardHoverText,
      isGCSMember: gcsMember,
      isGCSLegacy: gcsLegacy,
      suppModalOpen
    };
  }
);

export const getIsSummitCard = createSelector(
  [getPageAllowedLists, getPdpCardShortName],
  (allowedList, shortName) => {
    const projectSummitCards = allowedList.projectSummit || [];
    return projectSummitCards.indexOf(shortName) > -1;
  }
);

export const getStickySideRailData = (urlEep, dispatch) =>
  createSelector(
    [
      getPageFirstCardData,
      getPageLabels,
      getPdpLinks(urlEep),
      getCurrentCardInfo,
      getPznOfferList,
      getIsSummitCard,
      getCardColorInfoList,
      shouldShowDynamicAppPDP,
      isG171Experiment
    ],
    (
      cardData,
      labels,
      links,
      cardInfo,
      pznOfferList,
      isSummitCard,
      colorInfoList,
      showDynamicApp,
      isG171
    ) => {
      const colorInfo = colorInfoList.find(
        item => item.shortName === cardData.shortName
      );
      const cardArtUrl = decideCardArtUrl(
        get(cardData, 'appAssets.cardArt'),
        colorInfo
      );
      const annualFeeDisclaimer = colorInfo?.rubiksContent?.annualFeeDisclaimer;
      return buildStickySideRailProps({
        cardData,
        labels,
        links,
        cardInfo,
        dispatch,
        pznOfferList,
        isSummit: isSummitCard,
        cardArtUrl,
        annualFeeDisclaimer,
        showDynamicApp,
        isG171
      });
    }
  );

const getCardLabels = createSelector([getPageFirstCardData], cardData => {
  const { labels = [] } = cardData;
  return parseArrayToObject(labels, 'identifier', 'text');
});

export const getOverriddenLabels = createSelector(
  [getPageLabels, getCardLabels],
  (pageLabels = {}, cardLabels = {}) => ({ ...pageLabels, ...cardLabels })
);

export const getTopHeroData = urlEep =>
  createSelector(
    [
      getPageData,
      getPageFirstCardData,
      getPageLabels,
      getPdpLinks(urlEep),
      getCurrentCardInfo
    ],
    (pgData, cardData, labels, links, cardInfo) => {
      const backImgUrl = get(pgData, 'hero.assets[0].url');
      return { cardData, labels, links, cardInfo, backImgUrl };
    }
  );

export const getSummitData = createSelector(
  getPageFirstCardData,
  cardData => cardData?.featuredBenefitsTiles
);

export const getSummitDataDTM = createSelector(
  getPageFirstCardData,
  cardData => cardData?.featuredBenefitsTiles?.dtmEvents ?? []
);

export const supportUpgradeCta = createSelector(
  [getCurrentCardInfo, getPdpLinks()],
  (cardInfo, links) => {
    const { needUpgradeCta } = getUpgradeCtaProps(
      links,
      get(cardInfo, 'currentCard.pznOffer') || {},
      cardInfo.isGCSMember || cardInfo.isGCSLegacy
    );
    return needUpgradeCta;
  }
);

export const getDeltaHeadlineAssets = createSelector(
  [getCurrentCardInfo],
  cardInfo => {
    const breakpoints = [
      'backgroundImageMobile',
      'backgroundImageTablet',
      'backgroundImageDesktop'
    ];
    const breakpointWithAssets = {};
    breakpoints.forEach((breakpoint, index) => {
      breakpointWithAssets[breakpoint] =
        cardInfo?.currentCard?.benefitHeadline?.assets?.[index]?.url;
    });
    return breakpointWithAssets;
  }
);

export const getDeltaHeadlineContent = createSelector(
  [getCurrentCardInfo],
  cardInfo => ({
    deltaTitle: cardInfo?.currentCard?.benefitHeadline?.content?.title,
    deltaHeadlineHeading: cardInfo?.currentCard?.benefitHeadline?.content?.heading,
    deltaHeadlineDescription:
      cardInfo?.currentCard?.benefitHeadline?.content?.description,
    deltaHeadlineSubtext: cardInfo?.currentCard?.benefitHeadline?.content?.subtext
  })
);

export const getDeltaHeadlineDtmEvent = createSelector(
  [getCurrentCardInfo],
  cardInfo => {
    const { dtmEvents } = cardInfo?.currentCard?.benefitHeadline ?? {};
    const scrollEvent =
      parseArrayToObject(dtmEvents, 'tag')['deltaHeadlineImpression'] || {};
    return scrollEvent;
  }
);

export const isDeltaCard = createSelector([getCurrentCardInfo], cardInfo => {
  const deltaCardShortNames = [
    'platinum-delta-skymiles',
    'gold-delta-skymiles',
    'delta-reserve'
  ];
  const shortName = get(cardInfo, 'currentCard.shortName');
  return deltaCardShortNames.includes(shortName);
});

export const getPdPPznOfferEligibleDtm = createSelector(
  [getPageFirstCardData, getPznOfferEligibleDtm, getSpecialOfferList],
  (currCard, dtmObj, specialOffers) => {
    const matchedOffer = specialOffers.find(o => o.shortName === currCard.shortName);
    let resultDtm = dtmObj || {};
    if (matchedOffer) {
      const tmpArr = (resultDtm.eventAsset || '').split('_');
      resultDtm = {
        ...resultDtm,
        eventAsset: `${tmpArr[0]}_${tmpArr[1]}_special_${tmpArr[2]}`
      };
    }
    return resultDtm;
  }
);

export const getIsPayOverTimeCard = createSelector(
  [getPdpCardShortName, getPageAllowedLists],
  (pdpShortName, allowedlists) => {
    const isCardInList =
      (get(allowedlists, 'payOverTimeCards') || []).indexOf(pdpShortName) > -1;
    return isCardInList;
  }
);

export const getDeltaBenefitsTiles = createSelector(
  [getCurrentCardInfo],
  cardInfo => cardInfo?.currentCard?.deltaBenefitsTiles
);

export const getisTier2Offer = createSelector(
  [getPdpCardShortName, getPznOfferList],
  (shortName, pznOfferList) => {
    function isTier2Offer(offerButton) {
      const regex = /welcome|extra value/i;
      const offerButtonIncluesSpecial =
        offerButton.includes('special') || offerButton.includes('Special');
      const offerButtonContainsWelcomeOrExtraValue = regex.test(offerButton);
      return (
        offerButton !== '' &&
        (!offerButtonContainsWelcomeOrExtraValue || offerButtonIncluesSpecial)
      );
    }
    const pznOffer = (pznOfferList || []).find(o => o.shortName === shortName);
    return isTier2Offer(pznOffer?.offerButton ?? '');
  }
);

export const getOfferContent = createSelector(
  [getPdpCardShortName, getPznOfferList, getPageData, getIsProspect],
  (shortName, pznOfferList, pageData, isProspect) => {
    const {
      offerBoxSectionHeader: { prospect, cardMember }
    } = pageData;
    const offer = (pznOfferList || []).find(o => o.shortName === shortName);
    return {
      offerBoxSectionHeader: isProspect ? prospect : cardMember,
      offerHeader: offer?.header,
      offerDescription: offer?.description,
      dualOfferHeader: offer?.content?.[0]?.header,
      dualOfferDescription: offer?.content?.[0]?.description
    };
  }
);

export const getOfferBadgeText = createSelector(
  [getPznOfferList, getPdpCardShortName],
  (pznOfferList, shortName) => {
    const offer = (pznOfferList || []).find(o => o.shortName === shortName);
    const offerButtonText = offer?.offerButton ?? '';
    return capitalizeButtonText(formatSpecialOfferButtonText(offerButtonText));
  }
);

export const getHeroDtmEvents = createSelector([getPageFirstCardData], cardData => {
  const dtmEvents = parseArrayToObject(cardData?.hero?.dtmEvents ?? [], 'tag');
  return dtmEvents;
});

export const shouldShowDynamicAppPDP = createSelector(
  [getIsProspect, getPageDisallowedLists, isLoggedOut, getPdpCardShortName],
  (isProspect, disallowedlists, isLoggedOutCM, shortName) => {
    const isCardInList = disallowedlists?.noDynamicAppFlow.indexOf(shortName) > -1;
    return !isCardInList && (isProspect || isLoggedOutCM);
  }
);

export const getDynamicAppReinforcementBannerContent = createSelector(
  [getPageData, getPdpReinforcementBannerData, shouldShowDynamicAppPDP],
  (pageData, bannerData, showDynamicApp) => {
    const { dynAppReinforcementBanner } = pageData;
    const bannerDataCopy = { ...bannerData };
    if (!showDynamicApp) return bannerDataCopy;
    bannerDataCopy.title = dynAppReinforcementBanner?.title;
    bannerDataCopy.subTitle = dynAppReinforcementBanner?.subTitle;
    bannerDataCopy.learnMoreLink = dynAppReinforcementBanner?.learnMoreLink;
    bannerDataCopy.dtm = {
      ...dynAppReinforcementBanner?.dtm,
      eventAsset: `${dynAppReinforcementBanner?.dtm.eventAsset}${
        bannerDataCopy.shortName
      }`
    };
    return bannerDataCopy;
  }
);

export const hasBothColorPickerAndDynamicApp = cardShortName =>
  createSelector(
    [determineColorPicker(cardShortName), shouldShowDynamicAppPDP],
    (pickerInfo, showDynAppInSideRail) => {
      return pickerInfo?.showPicker && showDynAppInSideRail;
    }
  );

export const getStackedCarouselSettings = createSelector(
  getPageFirstCardData,
  cardData => cardData.deltaBenefitsTiles?.carouselSettings ?? {}
);

export const getStackedCarouselData = createSelector(
  getPageFirstCardData,
  cardData => {
    const content = cardData.deltaBenefitsTiles?.tilesContent;
    if (!Array.isArray(content)) return null;

    const firstSlideData = { top: content[0], bottom: content[1] };
    const secondSlideData = { top: content[2], bottom: content[3] };
    const stackedDataInOrder = [firstSlideData, secondSlideData];
    return stackedDataInOrder;
  }
);

export const getDynAppSideRailContent = createSelector(
  [getPageData, getPdpCardShortName],
  (pageData, shortName) => {
    return {
      ...pageData?.dynAppLeftRail,
      shortName: shortName
    };
  }
);

// selectors for LTO badges
export const getLtoOfferBadgeText = createSelector(
  [getPznOfferList, (_, shortName) => shortName],
  (offerList, shortName) => {
    const cardoffer = offerList.find(offer => offer.shortName === shortName) || {};
    const offerButtonText = cardoffer?.offerButton;
    const offerDates = cardoffer?.offerDataDate || {};
    return capitalizeButtonText(
      formatSpecialOfferButtonText(
        formatLTOOfferButtonText(offerDates, offerButtonText)
      )
    );
  }
);
